<script setup lang="ts">
import { equals, without } from 'ramda';

import type { LocationQuery, LocationQueryValue } from 'vue-router';

// icons
import { faExclamationCircle, faMapMarked } from '@fortawesome/pro-regular-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import { RBN_CITY_SEARCH, RBN_COUNTRY_SEARCH, RBN_FEDERAL_STATE_SEARCH, RBN_REGION_SEARCH, RBN_SEARCH, RBN_SEARCH_MAP } from '@/utils/constants';

// components
import CiSkeletonCampsiteTile from '@/components/skeleton/CiSkeletonCampsiteTile.vue';
import CiSkeletonItem from '@/components/skeleton/CiSkeletonItem.vue';
import CiSlotContent from '@/components/slots/CiSlotContent.vue';

/********************
 * COMPOSITIONS     *
 ********************/
const appStore = useAppStore();
const contentStore = useContentStore();
const getRouteBaseName = useRouteBaseName();
const localePath = useLocalePath();
const localeRoute = useLocaleRoute();
const route = useRoute();
const routeStore = useRouteStore();
const searchStore = useSearchStore();
const seoStore = useSeoStore();
const viewport = useViewport();
const { $gettext, interpolate, $pgettext } = useGettext();
const { dynamicHeadline } = useDynamicHeadline();
const { fetchError, fetchFailed, fetchStatus } = useFetchError();
const { getLocationContextForSerp } = useECommerceContext();
const { getMatchingSearchQueryForMap } = useMatchingSearchQueryForMap();
const { locale } = useI18n();
const { openModal, closeModal } = useCiModal();
const { requestAds } = useAds();
const { trackLinkClick, trackProductListView } = useTracking();

const availabilitySeparator = ref(-1);
const limit = ref(15);

const routeBaseName = computed(() => {
  return getRouteBaseName(route);
});

type OrderOptionShowIfFunction = () => boolean;

type OrderOption = {
  key: string;
  value: string;
  showIf?: OrderOptionShowIfFunction;
};

/********************
 * Ordering         *
 ********************/
const orderOptions: OrderOption[] = [
  { key: 'relevance', value: $pgettext('Order by', 'Recommendation') },
  { key: 'price', value: $pgettext('Order by', 'Price'), showIf: () => !!(route.query.from && route.query.until) },
  { key: 'rating', value: $pgettext('Order by', 'Rating') },
  { key: 'distance', value: $pgettext('Order by', 'Distance'), showIf: () => !!route.query.distance_from },
] as const;
type OrderOptionKeys = (typeof orderOptions)[number]['key'];
const orderSelected = ref<(typeof orderOptions)[number] | null>(null);
const orderStyle = ref('');
const orderStyles = shallowRef<Record<OrderOptionKeys, string>>({
  price: '',
  rating: '',
  relevance: '',
  distance: '',
});
const orderWidthTemplates = reactive<Record<OrderOptionKeys, HTMLElement | null>>({
  price: null,
  rating: null,
  relevance: null,
  distance: null,
});

const filteredOrderOptions = computed(() => {
  return orderOptions.filter(option => !option.showIf || option.showIf());
});

const searchResponseDistance = computed(() => data.value?.searchResponse?.distance || null);
const searchResponseDistanceOriginalCount = computed(() => searchResponseDistance.value?.original_count || 0);
const firstCampsiteIsExactLocation = computed(() => searchStore.resultItems && searchStore.resultItems[0].is_exact_location);

orderSelected.value = route.query.order
  ? orderOptions.find(item => item.key === route.query.order) || null
  : orderOptions.find(item => item.key === 'relevance') || null;

watch(() => route.query.order, setOrderOptionsStyles, { immediate: true });

function onOrderChange(value: string) {
  const query: LocationQueryValue = { ...route.query, order: value };

  if (query.order === 'relevance') {
    delete query.order;
  }

  delete query.offset;

  return navigateTo({
    name: route.name || undefined,
    params: route.params,
    query,
  });
}

function setOrderOptionsStyles(newValue: LocationQueryValue | LocationQueryValue[]) {
  orderStyle.value = orderStyles.value[newValue || 'relevance'];
}

function calculateOrderOptionsWidths() {
  for (let i = 0; i < orderOptions.length; i++) {
    const option = orderOptions[i];
    const ele = orderWidthTemplates[option.key];
    if (!ele) {
      nextTick(calculateOrderOptionsWidths);
      return;
    }
    const w = ele.offsetWidth + 50;
    orderStyles.value = {
      ...orderStyles.value,
      [option.key]: `width: ${w}px`,
    };
  }

  if (orderSelected.value) {
    orderStyle.value = orderStyles.value[orderSelected.value.key];
  }
}

/********************
 * Filter            *
 ********************/
const CiFilterModal = defineAsyncComponent({
  loader: () => import('@/components/filter/CiFilterPanel/CiFilterModal.vue'),
});

const {
  filterGroups,
  getFilterByName,
  openResetModal,
  setFilter,
} = useFilter();

const filteredGroups = computed(() => {
  const ratingGroups = ['rating', 'perfect_for', 'suitable_for', 'fanclub'];
  return filterGroups.filter(group => !ratingGroups.includes(group.name));
});

function showFilterModal() {
  openModal(
    {
      component: CiFilterModal,
      attrs: {
        name: 'CiFilterModal',
        onClose: () => {
          closeModal('CiFilterModal');
        },
      },
    },
    {
      clickToClose: true,
      contentClass: 'w-full lg:max-w-[700px] bg-white lg:rounded-lg p-0 flex items-center h-full overflow-hidden',
    },
  );
}

function showRatingFilterModal() {
  searchStore.selectedFilterGroup = null;

  const groupsToShow = ['rating', 'perfect_for', 'suitable_for'];
  const groupsToDisplay = filterGroups.filter(group => groupsToShow.includes(group.name));

  openModal(
    {
      component: CiFilterModal,
      attrs: {
        name: 'CiFilterModal',
        excludedFilter: ['rating_avg_overall__gte'],
        groupsToDisplay,
        showSidebar: false,
        onClose: () => {
          closeModal('CiFilterModal');
        },
      },
    },
    {
      clickToClose: true,
      contentClass: 'w-full lg:max-w-[700px] bg-white lg:rounded-lg p-0 flex items-center h-full overflow-hidden',
    },
  );
}

const promoName = computed(() => {
  if (!contentStore.contents.length) {
    return '';
  }
  return contentStore.contents[0]?.meta?.special_promotion?.name || '';
});

function getFilterDisplay(name: string, value) {
  const filter = getFilterByName(name);

  if (!filter) {
    return '';
  }

  if (filter.type === 'range' && filter.tooltip) {
    const calcValue = filter.multiplier ? filter.multiplier * value : value;
    const newValue = `(${interpolate(filter.tooltip.replace(/\{([^}]+)\}/g, '%{$1}'), {
      value: calcValue,
    })})`;
    return `${filter.label} ${newValue}`;
  } else if (filter.type === 'rating' && filter.displayValue) {
    const newValue = `(${interpolate(filter.displayValue.replace(/\{([^}]+)\}/g, '%{$1}'), {
      value,
    })})`;
    return `${filter.label} ${newValue}`;
  } else if (filter.type === 'bool' || filter?.type === 'bitwise') {
    return `${filter.label}`;
  } else if (filter.type === 'string') {
    return promoName.value || value;
  }
}

/********************
 * Map               *
 ********************/
const queryWithOnlyAvailabilitiesParameter = computed(() => {
  const queryCopy = { ...route.query };
  if (isAS.value) {
    queryCopy.only_availabilities = searchStore.showOnlyCampsitesWithAvailabilitiesOnMap ? 'true' : 'false';
  } else {
    delete queryCopy.only_availabilities;
  }
  return queryCopy;
});

function getCampsiteMapData(campsite: any) {
  return {
    id: campsite.id,
    name: campsite.name,
    rating_avg_overall: campsite.rating_avg_overall,
    rating_count: campsite.rating_count,
    showEffect: true,
    slug: campsite.slug,
  };
}

function showMapModal(campsite: any, passQuery: boolean = false) {
  const CiMapModal = defineAsyncComponent({
    loader: () => import('@/components/map/CiMapModal/CiMapModal.vue'),
  });

  let query = {};
  if (passQuery) {
    query = route.query;
  }

  openModal(
    {
      component: CiMapModal,
      attrs: {
        query: {
          ...getMatchingSearchQueryForMap({ entireListUrl: null, isAS: false }),
          ...query,
        },
        campsite: getCampsiteMapData(campsite),
        latitude: `${campsite.geo_position.lat}`,
        longitude: `${campsite.geo_position.lon}`,
        title: campsite.name,
        name: 'CiMapModal',
        onClose: () => {
          closeModal('CiMapModal');
        },
      },
    },
    {
      clickToClose: true,
      contentClass: 'max-w-[1140px] w-full h-[80%]',
    },
  );
}

/********************
 * INITIALIZATION    *
 ********************/
const { data, pending, refresh, error, status } = await useLazyAsyncData(`serp-${route.fullPath}`, async () => {
  contentStore.clearContent();
  const newQuery = { ...route.query };
  const originalParams = { ...route.params };
  const originalQuery = { ...route.query };

  const isCitySearch = routeBaseName.value === RBN_CITY_SEARCH;
  const isCountrySearch = routeBaseName.value === RBN_COUNTRY_SEARCH;
  const isFederalState = routeBaseName.value === RBN_FEDERAL_STATE_SEARCH;
  const isRegionSearch = routeBaseName.value === RBN_REGION_SEARCH;

  if (isCountrySearch) {
    newQuery.country = originalParams.countrySlug;
  }

  if (isCitySearch) {
    newQuery.city = originalParams.citySlug;
  }

  if (isFederalState) {
    newQuery.federal_state = originalParams.federalStateSlug;
  }

  if (isRegionSearch) {
    newQuery.region = originalParams.regionSlug;
  }

  delete newQuery.only_availabilities;

  const searchParams = { ...searchStore.searchParams };
  searchStore.setSearchParams({
    ...searchParams,
    acco_type: newQuery.acco_type || null,
    adults: newQuery.adults ? parseInt(newQuery.adults) : 2,
    children: newQuery.children || null,
    flex: newQuery.flex ? parseInt(newQuery.flex) : searchParams.flex,
    discounts: newQuery.discounts || null,
    from: newQuery.from || null,
    q: newQuery.federal_state || newQuery.region || newQuery.country || newQuery.city || newQuery.q || '',
    until: newQuery.until || null,
  });
  seoStore.distance = null;

  // Use normal limit param in maps view
  newQuery.limit = limit.value;
  const foundLocation = routeStore.foundLocation;
  const searchOrigin = routeStore.searchOrigin;
  const searchState = routeStore.searchState;
  const searchTerm = routeStore.searchTerm;

  if (searchState.isSet) {
    newQuery.foundLocation = foundLocation;
    newQuery.origin = searchOrigin;
    newQuery.originalSearchTerm = searchTerm;
    routeStore.setSearchState(null);
  }

  // if query contains federal_state and country, we need to use just federal_state
  if (newQuery.country && newQuery.federal_state) {
    delete newQuery.country;
  }

  if (newQuery.order === 'relevance') {
    delete newQuery.order;
  }

  if (import.meta.server) {
    /*
    #######################################
    ### t = tracking mode               ###
    #######################################
    TRACKING_MODE_NORMAL = '0'
    TRACKING_MODE_ONLY_TRACKING = '1'
    TRACKING_MODE_NO_TRACKING = '2'
    */
    newQuery.t = '2';
  }

  // order
  orderSelected.value = route.query.order
    ? orderOptions.find(item => item.key === route.query.order) || null
    : orderOptions.find(item => item.key === 'relevance') || null;

  availabilitySeparator.value = -1;

  try {
    // trigger search
    const response = await searchStore.getSearchResults({ query: newQuery, lang: locale.value });
    let alternateObj = {};

    const transformedSearchResponse = {};

    if (response?.meta?.location?.name) {
      transformedSearchResponse.distance = response.meta.distance || null;
      transformedSearchResponse.headline = response.meta.location.name;
      transformedSearchResponse.location = response.meta.location;
      transformedSearchResponse.slugs = response.meta.location.slugs;

      seoStore.distance = response.meta.distance;
      searchStore.setSearchParams({
        ...searchStore.searchParams,
        q: response.meta.location.name,
      });
    }

    if (response?.meta?.location?.slugs) {
      alternateObj = { ...response.meta.location.slugs };
    }

    if (response?.meta?.location?.country) {
      transformedSearchResponse.country = response.meta.location.country;
      alternateObj = {
        current: response.meta.location.slugs,
        parent: response.meta.location.country.slugs,
      };
    }

    if (response?.meta?.location?.federal_state) {
      transformedSearchResponse.federal_state = response.meta.location.federal_state;
    }

    seoStore.alternateTags = alternateObj;

    /******************************
     * create dynamic meta params  *
     ******************************/
    const dynamicMetaParams = {};
    if (response?.count !== null) {
      dynamicMetaParams.resultCount = response.count;
    }

    if (response?.meta?.location?.seo) {
      Object.assign(dynamicMetaParams, response.meta.location.seo);
      delete dynamicMetaParams.avg_accommodation_price;
      delete dynamicMetaParams.avg_pitch_price;
      delete dynamicMetaParams.avg_reference_price;
      delete dynamicMetaParams.top_rated_campsites;
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      Object.keys(dynamicMetaParams).forEach(key => dynamicMetaParams[key] == null && delete dynamicMetaParams[key]);
    }

    dynamicMetaParams.searchTerm = transformedSearchResponse.headline || originalQuery.q || '';
    seoStore.setDynamicMetaParam(dynamicMetaParams);

    /************************************
     * adjust search term params start  *
     * (for mixed translations)         *
     ************************************/
    if (isCountrySearch || isCitySearch || isRegionSearch || isFederalState || transformedSearchResponse.slugs) {
      delete originalQuery.country;
      delete originalQuery.city;
      delete originalQuery.federal_state;
      delete originalQuery.region;
      let searchTerms = [];

      if (isRegionSearch) {
        searchTerms = [originalParams.regionSlug];
      } else if (isCountrySearch) {
        searchTerms = [originalParams.countrySlug];
      } else if (isCitySearch) {
        searchTerms = [originalParams.citySlug];
      } else if (isFederalState) {
        searchTerms.push(originalParams.countrySlug);
        searchTerms.push(originalParams.federalStateSlug);
      }

      const paramOne = searchTerms?.[0];
      const paramTwo = searchTerms?.[1];
      const slugAtLang = transformedSearchResponse.slugs?.[locale.value];
      const countrySlugAtLang = transformedSearchResponse.country?.slugs?.[locale.value];

      if (isCountrySearch) {
        if (searchTerms.length === 1 && slugAtLang !== paramOne) {
          const newRoute = localeRoute({
            name: RBN_COUNTRY_SEARCH,
            params: {
              countrySlug: slugAtLang,
            },
            query: originalQuery,
          });
          if (import.meta.client) {
            return navigateTo(newRoute, { replace: true });
          }
        }
      } else if (isCitySearch) {
        if (searchTerms.length === 1 && slugAtLang !== paramOne) {
          const newRoute = localeRoute({
            name: RBN_CITY_SEARCH,
            params: {
              citySlug: slugAtLang,
            },
            query: originalQuery,
          });
          if (import.meta.client) {
            return navigateTo(newRoute, { replace: true });
          }
        }
      } else if (isFederalState) {
        if (searchTerms.length === 2 && (countrySlugAtLang !== paramOne || slugAtLang !== paramTwo)) {
          const newRoute = localeRoute({
            name: RBN_FEDERAL_STATE_SEARCH,
            params: {
              countrySlug: countrySlugAtLang,
              federalStateSlug: slugAtLang,
            },
            query: originalQuery,
          });
          if (import.meta.client) {
            return navigateTo(newRoute, { replace: true });
          }
        }
      } else if (isRegionSearch) {
        if (searchTerms.length === 1 && slugAtLang !== paramOne) {
          const newRoute = localeRoute({
            name: RBN_REGION_SEARCH,
            params: { regionSlug: slugAtLang },
            query: originalQuery,
          });
          if (import.meta.client) {
            return navigateTo(newRoute, { replace: true });
          }
        }
      }
    }

    /***********************************
     * build url for content service   *
     ***********************************/
    let content;
    if (transformedSearchResponse.slugs?.en) {
      const routeParamsForContentService = {
        ...(isRegionSearch && { regionSlug: transformedSearchResponse.slugs.en }),
        ...(isFederalState && {
          countrySlug: transformedSearchResponse.country.slugs.en,
          federalStateSlug: transformedSearchResponse.slugs.en,
        }),
        ...(isCountrySearch && { countrySlug: transformedSearchResponse.slugs.en }),
        ...(isCitySearch && { citySlug: transformedSearchResponse.slugs.en }),
      };

      const englishRoute = localeRoute({ params: { ...routeParamsForContentService }, query: originalQuery }, 'en');
      const englishBaseName = englishRoute && getRouteBaseName(englishRoute);
      const sortedParams = englishBaseName && cleanAndSortParams(englishBaseName, englishRoute?.query, true);
      // --------------------------------------------------------------------
      // see rule for content url in mixin/seo.js
      // --------------------------------------------------------------------

      content = await contentStore.getContent({
        url: `${englishRoute?.path}${sortedParams}`,
        lang: locale.value,
      });
    } else if (route.query.promo) {
      content = await contentStore.getContent({
        url: `${route.path}?promo=${encodeURIComponent(route.query.promo as string)}`,
        lang: locale.value,
      });
    }

    return {
      contentResponse: content,
      originalResponse: response,
      searchResponse: transformedSearchResponse,
    };
  } catch (error) {
    fetchError(error);
    return error;
  }
});

if (error.value) {
  throw createError({ statusCode: error.value?.statusCode, message: error.value.statusMessage });
}

/********************
 * REFS & VARS       *
 ********************/
const isMobileView = computed(() => {
  return viewport.isLessThan('md');
});

const headline = computed(() => {
  return pending.value ? i18nSearchInProgress : dynamicHeadline.value;
});

const subline = computed(() => {
  if (pending.value) {
    return $gettext('Searching…');
  }

  let s = '';

  if (
    isAS.value
    && (searchStore.resultCount || searchStore.resultCount === 0)
    && (searchStore.resultAvailabilityCount === 0 || searchStore.resultAvailabilityCount > 0)
  ) {
    const translated = $gettext('%{ availabilityCount } out of %{ resultCount } with availabilities');
    s = interpolate(translated, {
      availabilityCount: searchStore.resultAvailabilityCount,
      resultCount: searchStore.resultCount,
    });
  } else if (
    (!isAS.value && (searchStore.resultCount || searchStore.resultCount === 0))
    || (isAS.value && !searchStore.resultAvailabilityCount)
  ) {
    const translated = $gettext('%{ n } campsites');
    s = interpolate(translated, { n: searchStore.resultCount });
  }

  return s;
});

const breadcrumb = computed(() => {
  const items = [];
  const isCountrySearch = routeBaseName.value === RBN_COUNTRY_SEARCH;
  const isCitySearch = routeBaseName.value === RBN_CITY_SEARCH;
  const isFederalState = routeBaseName.value === RBN_FEDERAL_STATE_SEARCH;
  const isRegionSearch = routeBaseName.value === RBN_REGION_SEARCH;
  const isCampsiteSearch = routeBaseName.value === RBN_CAMPSITE_SEARCH;

  if (isRegionSearch) {
    items.push({
      name: $gettext('Popular region'),
      class: 'breadcrumb-item--chevron',
      to: localePath({
        name: 'popularRegion',
      }),
    });
  }

  if (isCountrySearch) {
    items.push({
      name: $gettext('Country'),
      class: 'breadcrumb-item--chevron',
      to: localePath({
        name: 'country',
      }),
    });
  }

  if (!data.value) {
    return [];
  }

  if (isCitySearch) {
    items.push({
      name: $gettext('Country'),
      class: 'breadcrumb-item--chevron',
      to: localePath({
        name: 'country',
      }),
    });

    if (data.value.searchResponse?.country) {
      items.push({
        name: data.value.searchResponse.country?.name,
        class: 'breadcrumb-item--chevron',
        to: localePath({
          name: RBN_COUNTRY_SEARCH,
          params: {
            countrySlug: data.value.searchResponse.country.slugs?.[locale.value],
          },
        }),
      });

      if (data.value?.searchResponse.federal_state) {
        items.push({
          name: data.value.searchResponse.federal_state.name,
          class: 'breadcrumb-item--chevron',
          to: localePath({
            name: RBN_FEDERAL_STATE_SEARCH,
            params: {
              countrySlug: data.value.searchResponse.country.slugs?.[locale.value],
              federalStateSlug: data.value.searchResponse.federal_state.slugs?.[locale.value],
            },
          }),
        });
      }
    }
  }

  if (isFederalState) {
    items.push({
      name: $gettext('Country'),
      class: 'breadcrumb-item--chevron',
      to: localePath({
        name: 'country',
      }),
    });

    if (data.value.searchResponse?.country) {
      items.push({
        name: data.value.searchResponse.country.name,
        class: 'breadcrumb-item--chevron',
        to: localePath({
          name: RBN_COUNTRY_SEARCH,
          params: {
            countrySlug: data.value?.searchResponse.country.slugs?.[locale.value],
          },
        }),
      });
    }
  }

  if (routeBaseName.value === RBN_SEARCH) {
    items.push({
      name: $gettext('Search'),
      class: 'breadcrumb-item--chevron',
      to: route.query.q ? localePath({ name: RBN_SEARCH }) : null,
    });

    if (route.query.q) {
      items.push({
        name: route.query.q,
        class: 'breadcrumb-item--chevron',
        to: null,
      });
    }
  }

  if (data.value.searchResponse?.location) {
    items.push({
      name: data.value.searchResponse.location.name,
      class: 'breadcrumb-item--chevron',
      to: null,
    });
  }

  if (isCampsiteSearch && !items.length) {
    items.push({
      name: $gettext('Europe'),
      class: 'breadcrumb-item--chevron',
      to: null,
    });
  }

  return items;
});

const isAS = computed(() => {
  const isASearch = !!(route.query.from || route.query.until);
  return isASearch;
});

watchEffect(() => {
  appStore.floorAdActive = true; // !isAS.value;
});

const i18nSearchInProgress = $gettext('Searching…');

const locationName = computed(() => {
  if (!breadcrumb.value || !breadcrumb.value.length) {
    return '';
  }
  return breadcrumb.value[breadcrumb.value.length - 1].name || '';
});

const i18nNoCampsitesFound = $gettext('We could not find any campsites that match your search criteria.');

const i18nSearchDistanceExtendedTextNoResults = computed(() => {
  if (!searchResponseDistance.value) {
    return '';
  }
  return interpolate(
    $gettext(
      'Your search for campsites in %{ location } did not give any direct results. '
      + 'Therefore we have extended the search radius by %{ value } %{ unit }.',
    ),
    {
      location: locationName.value,
      ...searchResponseDistance.value,
    },
  );
});

const i18nSearchDistanceExtendedTextTooFewResults = computed(() => {
  if (!searchResponseDistance.value) {
    return '';
  }
  return interpolate(
    $gettext(
      'Other campsites within a %{ value } %{ unit } radius',
    ),
    {
      location: locationName.value,
      ...searchResponseDistance.value,
    },
  );
});

// prettier-ignore
const i18nNoAvailabilityInfo = $pgettext('Information on SERP between CPs with or without availabilities',
  'There are currently no bookable offers for these campsites. But something might still be available – just send a request via camping.info.',
);

const showFAQ = computed(() => {
  const isCitySearch = routeBaseName.value === RBN_CITY_SEARCH;
  const isCountrySearch = routeBaseName.value === RBN_COUNTRY_SEARCH;
  const isFederalState = routeBaseName.value === RBN_FEDERAL_STATE_SEARCH;
  const isRegionSearch = routeBaseName.value === RBN_REGION_SEARCH;
  const hasQueryWithoutSearch = without(
    ['backend', 'ciskipcache', ...Object.keys(searchStore.searchParams)],
    Object.keys(route.query),
  );
  return !hasQueryWithoutSearch.length && (isCountrySearch || isCitySearch || isFederalState || isRegionSearch);
});

const firstCpWithoutAvailabilities = computed(() => {
  return searchStore.resultItems?.findIndex(campsite => !hasAvailabilities(campsite));
});

const resultsFilterByAvailability = computed(() => {
  const filteredResults = {
    availabilities: [],
    noAvailabilities: [],
  };

  searchStore.resultItems.forEach((item) => {
    if (item.availabilities.flex || item.availabilities.exact) {
      filteredResults.availabilities.push(item);
    } else {
      filteredResults.noAvailabilities.push(item);
    }
  });
  return filteredResults;
});

const resultsWithAvailabilitiesOnlyTop = computed(() => {
  return !resultsFilterByAvailability.value.availabilities.some(item => !item.is_top);
});

/********************
 * HOOKS             *
 ********************/
onMounted(() => {
  searchStore.serpScrollTop = true;
  calculateOrderOptionsWidths();
  nextTick(function () {
    searchStore.redoLastSearch(locale.value);
  });
});

onBeforeUnmount(() => {
  clearNuxtData(`serp-${route.fullPath}`);
  searchStore.serpScrollTop = false;
  // this.$modal.hide('CiMapModal'); // done globally in modal plugin
});

/********************
 * WATCHER           *
 ********************/
watch(() => route.query, onQueryChange, { immediate: false });

watch([() => status.value, () => pending.value], ([status, pending]) => {
  if (status === 'success' && !pending) {
    sendEcommerceTracking();

    nextTick(() => {
      // refresh ad slots
      requestAds();
    });
  }
}, { immediate: true });

/********************
 * FUNCTIONS         *
 ********************/
function onQueryChange(newValue: LocationQuery, oldValue: LocationQuery) {
  if (equals(newValue, oldValue)) {
    searchStore.serpScrollTop = true;
    return;
  }

  if (searchStore.serpScrollTop) {
    window.scrollTo({ top: 0 });
  }

  searchStore.serpScrollTop = true;
  refresh();
}

function getFlexAvailabilitiesForCp(slug: string) {
  if (!searchStore.flexAvailabilities[slug]) {
    return null;
  }
  return searchStore.flexAvailabilities[slug].flex || null;
}

function onShowFlexClicked(campsite: any) {
  searchStore.getAvailabilities({ slug: campsite.slug, lang: locale.value, query: searchStore.searchParams });
}

function openDateSearch(event: MouseEvent) {
  const { id, classList, innerHTML } = event.target as HTMLElement;
  // const elementTarget = null;
  // const context = null;

  window.scrollTo({ top: 0, behavior: 'smooth' });

  if (searchStore.searchOpenType !== 'date') {
    searchStore.searchOpenType = 'date';
  }

  trackLinkClick({
    targetUrl: 'search-date',
    elementId: id,
    elementClasses: Array.from(classList),
    elementContent: innerHTML,
  });
}

function hasAvailabilities(campsite: any) {
  if (!campsite.availabilities) {
    return false;
  }

  return campsite.availabilities.exact || campsite.availabilities.flex;
}

function allowSponsored(campsite: any) {
  const hA = hasAvailabilities(campsite);
  return !hA ? true : !resultsWithAvailabilitiesOnlyTop.value;
}

function sendEcommerceTracking() {
  const products = [];
  const campsiteContexts = [];

  searchStore.resultItems.forEach((item, idx) => {
    products.push({
      id: item.camping_id.toString(),
      name: item.name,
      category: 'campsite',
      price: Math.round((item.price?.price_off_season || item.price?.price1 || 0) * 100) / 100,
      currency: 'EUR',
      position: idx + 1 + parseInt((route.query?.offset as string) || '0'),
    });

    campsiteContexts.push({
      schema: 'iglu:com.camparound/campinginfo_campsite/jsonschema/1-0-1',
      data: {
        booked_products: item.purchases || null,
        camping_id: item.camping_id,
        civ2_id: item.id,
        slug: item.slug,
      },
    });
  });

  const context = [...campsiteContexts];

  if (searchStore.location) {
    context.push(...getLocationContextForSerp(toRaw(searchStore.location)));
  }

  trackProductListView({
    products,
    name: 'SERP',
    context,
  });
}
</script>

<template>
  <div
    v-if="!fetchFailed"
    class="serp relative flex flex-col"
  >
    <div
      v-if="viewport.isGreaterOrEquals('xl')"
      class="skyscraper absolute size-full"
    >
      <div class="container h-full">
        <div class="flex h-full">
          <CiAdSlot
            id="camping_sidebar_left"
            class="ad--skyscraper ad--skyscraper-left sticky top-0"
            style="top: 50px; z-index: 31"
          />
          <CiAdSlot
            id="camping_sidebar_1"
            class="ad--skyscraper ad--skyscraper-right sticky top-0"
            style="top: 50px; z-index: 31"
          />
        </div>
      </div>
    </div>

    <!-- headline -->
    <CiHeadline
      ref="headlineRef"
      :headline="headline"
      class="search__headline sm:my-3 lg:mb-6 lg:mt-4"
    />

    <!-- search input -->
    <div class="mb-4 bg-gray-10 lg:mb-0">
      <div
        class="z-10 mx-auto my-0 w-full max-w-full px-[15px] sm:max-w-[540px] md:max-w-[720px] lg:max-w-[940px]"
      >
        <CiSearch
          :is-mobile="isMobileView"
          collapsible
        />
      </div>
    </div>

    <!-- mobile filter -->
    <div class="container mb-2 grid grid-cols-2 gap-2 lg:hidden">
      <CiButton
        tag="NuxtLink"
        :href="
          localePath({
            name: RBN_SEARCH_MAP,
            query: { ...getMatchingSearchQueryForMap({ isAS: isAS }), ...queryWithOnlyAvailabilitiesParameter },
          })
        "
        class="flex"
      >
        <CiAwesomeIcon
          :icon="faMapMarked"
          ratio="0.6"
          class="mr-2"
        />
        {{ $gettext('Go to Map') }}
      </CiButton>
      <CiButton
        tag="button"
        class="w-full"
        @click="showFilterModal"
      >
        <img
          src="~/assets/svg/icon-filter.svg"
          class="icon mr-2"
          alt="icon filter"
        />
        <span class="flex flex-row whitespace-nowrap">
          <span class="block">{{ $gettext('Filter') }}</span>
          <span
            v-if="searchStore.filterCount"
            class="ml-1 block"
          >({{ searchStore.filterCount }})</span>
        </span>
      </CiButton>
    </div>

    <!-- subline -->
    <section
      v-if="subline"
      class="bg-gray-10 py-2 text-[#717171]"
    >
      <div class="container relative">
        <h2
          class="m-0 text-center text-sm font-normal lg:text-base"
          v-html="subline"
        />
      </div>
    </section>

    <div class="flex justify-center border-t-4 border-gray-20 bg-gray-20 px-4 md:border-t-[10px]" />

    <div class="search-result relative bg-gray-10 pb-12 pt-0 md:pt-0">
      <div class="container">
        <CiSlotContent
          :extra="{ type: 'wide' }"
          slug="top"
        />
        <div class="flex flex-row flex-nowrap py-1 lg:gap-x-[30px]">
          <div v-if="viewport.isGreaterThan('md')" class="hidden items-center overflow-hidden lg:flex">
            <CiBreadcrumb
              :items="breadcrumb"
              background="gray"
            />
          </div>
          <div class="ml-auto flex shrink-0 flex-row items-center">
            <label for="ordering">{{ $gettext('Sorted by') }}</label>
            <div
              class="flex flex-col"
              :style="orderStyle"
            >
              <CiSelect
                name="ordering"
                class="!text-base"
                :options="filteredOrderOptions"
                :value="orderSelected"
                layout="simple"
                @change="onOrderChange"
              />
              <div
                class="invisible"
                style="min-width: 0; height: 0"
              >
                <div
                  v-for="order of orderOptions"
                  :key="order.key"
                >
                  <span :ref="el => (orderWidthTemplates[order.key] = el as HTMLElement)">
                    {{ order.value }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-12 lg:gap-x-[30px]">
          <div class="col-span-12 hidden lg:col-span-3 lg:block">
            <nuxt-link
              v-if="viewport.isGreaterThan('md')"
              :to="
                localePath({
                  name: RBN_SEARCH_MAP,
                  query: {
                    ...getMatchingSearchQueryForMap({ isAS: isAS }),
                    ...queryWithOnlyAvailabilitiesParameter,
                  },
                })
              "
              class="relative mb-6 flex h-[75px] w-full items-center justify-center overflow-hidden rounded border border-warning bg-[rgba(216,216,216,0.2)] hover:no-underline xl:h-[95px]"
              append
            >
              <picture>
                <source
                  srcset="~/assets/img/map_placeholder.webp"
                  type="image/webp"
                />
                <source
                  srcset="~/assets/img/map_placeholder.jpg"
                  type="image/jpg"
                />
                <img
                  src="~/assets/img/map_placeholder.jpg"
                  class="absolute left-0 top-0"
                  alt="go to map"
                />
              </picture>
              <div class="group z-10 flex items-center rounded-[0.313em] bg-white px-4 py-2 text-sm font-medium leading-5 shadow-ci-sm hover:bg-dark-green [&>.icon]:fill-warning [&>.icon]:hover:fill-white">
                <span class="text-warning group-hover:text-white">{{ $gettext('Go to Map') }}</span>
                <CiAwesomeIcon
                  :icon="faChevronRight"
                  ratio="0.6"
                />
              </div>
            </nuxt-link>
            <client-only>
              <LazyCiFilterPanel
                v-if="viewport.isGreaterThan('md')"
                :groups="filteredGroups"
                :selected-filter="searchStore.selectedFilter"
                :selected-filter-group="searchStore.selectedFilterGroup"
                @set-filter-group="(group: string) => (searchStore.selectedFilterGroup = group)"
                @input="setFilter"
                @reset="openResetModal"
                @detail-rating="showRatingFilterModal"
              />
              <template #fallback>
                <CiSkeletonItem class="h-[1000px] rounded" />
              </template>
            </client-only>
          </div>
          <div class="col-span-12 lg:col-span-9">
            <LazyVisibleCiFanclubFilter
              hydrate-on-visible
              @change="setFilter"
            />

            <div v-if="viewport.isGreaterThan('md') && Object.keys(searchStore.selectedFilter).length">
              <div
                v-for="(value, name, index) in searchStore.selectedFilter"
                :key="index"
                class="mb-2 mr-2 inline-flex items-center rounded-[5px] bg-gray-30 p-2"
              >
                <span class="text-small-book"> {{ getFilterDisplay(name, value) }}</span>
                <div
                  class="ml-2 cursor-pointer"
                  @click="setFilter({ filter: name, value: 0 })"
                >
                  <CiAwesomeIcon
                    :icon="faTimes"
                    ratio="0.6"
                  />
                </div>
              </div>
            </div>

            <!-- loading skeleton -->
            <section v-if="pending">
              <ul class="m-0 list-none p-0">
                <li
                  v-for="index in 15"
                  :key="index"
                  class="mb-6"
                >
                  <CiSkeletonCampsiteTile />
                </li>
              </ul>
            </section>

            <!-- result items -->
            <section
              v-else
              ref="searchResultList"
            >
              <ul class="m-0 list-none space-y-6 p-0">
                <li
                  v-if="!searchStore.resultItems?.length"
                >
                  <CiInfoPanel
                    :condition="true"
                    type="info"
                  >
                    <div class="flex items-center">
                      <CiAwesomeIcon
                        class="mx-6 fill-info"
                        :icon="faExclamationCircle"
                        ratio="1"
                      />
                      <p class="m-0 p-0">
                        {{ i18nNoCampsitesFound }}
                      </p>
                    </div>
                  </CiInfoPanel>
                </li>
                <template
                  v-for="(campsite, idx) in searchStore.resultItems"
                  :key="`campsite-${campsite.id}`"
                >
                  <li v-if="idx === 0 && searchResponseDistance && !firstCampsiteIsExactLocation && searchStore.resultCurrentPage < 2">
                    <CiInfoPanel
                      :condition="true"
                      type="info"
                      class="!mb-0"
                    >
                      <div class="flex items-center">
                        <CiAwesomeIcon
                          class="mx-6 fill-info"
                          :icon="faExclamationCircle"
                          ratio="1"
                        />
                        <p class="m-0 p-0">
                          <span>{{ i18nSearchDistanceExtendedTextNoResults }}</span>
                        </p>
                      </div>
                    </CiInfoPanel>
                  </li>
                  <li
                    v-else-if="searchResponseDistance && firstCampsiteIsExactLocation && searchResponseDistanceOriginalCount === idx && searchStore.resultCurrentPage < 2"
                    :class="{ 'mt-6': !!searchResponseDistanceOriginalCount }"
                  >
                    <CiInfoPanel
                      :condition="true"
                      type="info"
                      class="!mb-0"
                    >
                      <div class="flex items-center">
                        <CiAwesomeIcon
                          class="mx-6 fill-info"
                          :icon="faExclamationCircle"
                          ratio="1"
                        />
                        <p class="m-0 p-0">
                          <span v-if="searchResponseDistanceOriginalCount">{{ i18nSearchDistanceExtendedTextTooFewResults }}</span>
                          <span v-else>{{ i18nSearchDistanceExtendedTextNoResults }}</span>
                        </p>
                      </div>
                    </CiInfoPanel>
                  </li>
                  <li>
                    <template v-if="route.query.from && firstCpWithoutAvailabilities === idx">
                      <CiInfoPanel
                        :condition="true"
                        type="info"
                      >
                        <div class="flex items-center">
                          <CiAwesomeIcon
                            class="mx-6 fill-info"
                            :icon="faExclamationCircle"
                            ratio="1"
                          />
                          <p class="m-0 p-0">
                            {{ i18nNoAvailabilityInfo }}
                          </p>
                        </div>
                      </CiInfoPanel>
                    </template>
                    <CiCampsiteTileAs
                      :campsite="campsite"
                      :snowplow-context="{
                        bannerId: 'search',
                        position: idx + 1,
                      }"
                      :flex-availabilities="getFlexAvailabilitiesForCp(campsite.slug)"
                      :allow-sponsored="allowSponsored(campsite)"
                      @location-clicked="showMapModal(campsite)"
                      @show-flex="onShowFlexClicked(campsite)"
                    />
                    <div
                      v-if="!isAS && idx === 4"
                      class="mt-6 flex justify-center"
                    >
                      <CiButton
                        tag="button"
                        variant="warning"
                        class="self-center"
                        @click="openDateSearch"
                      >
                        {{ $gettext('Enter travel period and find bookable campsites') }}
                      </CiButton>
                    </div>

                    <CiAdSlot
                      v-if="idx === 2"
                      id="camping_content_1"
                      class="ad--h-600-300-250 mt-4"
                    />
                  </li>
                </template>
              </ul>
            </section>

            <!-- pagination -->
            <section
              v-if="!pending"
              class="search-result__pagination mt-12"
            >
              <CiPagination
                v-if="searchStore.resultTotalPages > 1"
                :offset="limit"
                :current-page="searchStore.resultCurrentPage"
                :max-visible-buttons="searchStore.resultVisiblePagerButtons"
                :total-pages="searchStore.resultTotalPages"
              />
            </section>
          </div>

          <div
            class="col-span-12"
          >
            <CiAdSlot
              id="camping_content_2"
              class="ad--h-480--400--250 mt-4"
            />
          </div>

          <div class="search__slot-content-bottom col-span-12 my-4">
            <CiSlotContent :extra="{ type: 'wide' }" />
            <CiSlotContent
              :extra="{ type: 'wide' }"
              slug="below-pagination"
            />

            <CiSerpFaq
              v-if="showFAQ"
              :location="searchStore.resultMeta.location"
              :search-result="searchStore.resultItems"
            />

            <CiAdSlot
              v-if="contentStore.contents.length"
              id="camping_content_3"
              class="ad--h-250 mt-6"
            />
          </div>

          <!-- breadcrumb -->
          <div class="col-span-12 block lg:hidden">
            <CiBreadcrumb
              :items="breadcrumb"
              background="gray"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <CiErrorView
    v-else
    :error="fetchStatus"
  />
</template>

<style lang="postcss" scoped>
.search__headline :deep(h1) {
  @apply text-base lg:text-3xl max-sm:min-h-[48px];
}
</style>
